import { Button, ButtonGroup } from "@components/button";
import { yearMonthSortFunction } from "@components/inputs/date/utils";
import { isVisibleByPath } from "@components/smart/FieldInfo";
import SelectionTableViewDialog from "@components/smart/selectionTableView/SelectionTableViewDialog";
import {
    ActionType,
    addEmptyLineItem,
    addNewLineItem,
    ISmartFastEntriesActionEvent
} from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrDeductionEntity,
    IPrDepositedDeductionEntity,
    IPrEmployeeEntity,
    PrDeductionBankAccountEntity,
    PrDeductionBusinessPartnerEntity,
    PrDeductionEmployeeEntity,
    PrDeductionEntity,
    PrDeductionOtherPaymentEntity,
    PrDeductionTemporalEntity
} from "@odata/GeneratedEntityTypes";
import {
    DocumentTypeCode,
    PrChildSupportDeductionTypeCode,
    PrDeductionCalculationTypeCode,
    PrDeductionTypeCode
} from "@odata/GeneratedEnums";
import { handleCurrentTemporalPropChange } from "@odata/TemporalUtils";
import {
    getBankBlurArgs,
    handleBankFieldChangeWithoutSavedAccount,
    hasEmptyBankAccount,
    prepareSavedAccounts,
    SAVED_ACCOUNTS_PATH
} from "@pages/banks/bankAccounts/BankAccounts.utils";
import { setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import { getDefinitions as getDepositClosingDef } from "@pages/payroll/deduction/DepositClosingDef";
import DepositClosingFormView from "@pages/payroll/deduction/DepositClosingFormView";
import { getDefinitions as getOtherPaymentsDef } from "@pages/payroll/deduction/OtherPaymentsDef";
import OtherPaymentsFormView from "@pages/payroll/deduction/OtherPaymentsFormView";
import PairEmployeeTableView from "@pages/payroll/deduction/PairEmployeeTableView";
import { getDefinitions as getEmloyeesDef } from "@pages/payroll/employee/Employee.def";
import { handleBankAccountOrIBANBlur } from "@utils/BankUtils";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { DATE_MAX, getUtcDayjs } from "../../../types/Date";
import { FormStorage, IFormStorageDefaultCustomData, IGroupStatus } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import PlugAndPlayForm from "../../../views/formView/PlugAndPlayForm";
import {
    ADD_EMPLOYEE_ITEM,
    ADD_OTHER_PAYMENT_ACTION,
    getDateEndFromLastDeduction,
    hasDepositClosingDocumentBeenLiquidated,
    IDeductedAmounts,
    IDepositedDeduction,
    INSOLVENCY_DEPOSIT_CLOSING,
    isDebtTypeOfChildSupport,
    loadDepositedDeductions,
    loadDepositedDistrainmentAmounts,
    isDepositDeductionClosed
} from "@pages/payroll/deduction/Deduction.utils";

export interface IDeductionCustomData extends IFormStorageDefaultCustomData {
    pairedDialogOpened?: boolean;
    depositClosingDialogOpen?: boolean;
    otherPaymentsDialogOpen?: boolean;
    depositedDeductions?: IDepositedDeduction[];
    deducted?: IDeductedAmounts[];
}

class DeductionFormView extends FormViewForExtend<IPrDeductionEntity, IFormViewProps<IPrDeductionEntity, IDeductionCustomData>> {
    static contextType = AppContext;
    static defaultProps = {
        title: "Deduction:Form.Title",
        formProps: {
            shouldHideVariant: true
        }
    };

    async onAfterLoad(): Promise<void> {
        const storage = this.props.storage.getThis();
        const bindingContext = this.props.storage.data.bindingContext;

        if (bindingContext.isNew()) {
            if (!this.entity.NumberOurs) {
                await setMatchingNumberRange(storage);
            }
        }

        // todo: why this condition??
        if (storage.data.definition.fieldDefinition[SAVED_ACCOUNTS_PATH]) {
            prepareSavedAccounts({
                storage,
                type: DocumentTypeCode.InvoiceReceived,
                businessPartner: this.entity.Recipient?.BusinessPartner ?? {},
                businessPartnerPath: PrDeductionEntity.Recipient,
                account: this.entity.BankAccount,
                isInit: true
            });
        }
        await this.loadTabsData();

        storage.refresh();
        super.onAfterLoad();
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        const { storage } = this.props;
        if (actionId === ADD_OTHER_PAYMENT_ACTION) {
            storage.setCustomData({ otherPaymentsDialogOpen: true });
            storage.refresh(true);
        }

        return super.handleCustomHeaderAction(actionId);
    }

    setTabsVisibility = (): void => {
        const storage = this.props.storage.getThis();
        const { depositedDeductions, deducted } = storage.getCustomData() as IDeductionCustomData;
        // ignoreTabsVisibility = true prevents updateTabsVisibility from overwriting our custom visibleTabs
        storage.options.ignoreTabsVisibility = true;
        const entity = storage.data.entity as IPrDeductionEntity;
        const bindingContext = storage.data.bindingContext;

        const visibleTabs: Record<string, boolean> = {
            deponation: !!depositedDeductions?.length,
            deducted: deducted?.length > 0,
            otherPayments: entity.OtherPayments?.length > 0,
            files: !!entity.DepositClosingDocument?.Id,
            unpaidChildSupport: entity.TypeCode === PrDeductionTypeCode.ChildSupport
                && isDebtTypeOfChildSupport({ storage, bindingContext, context: this.context as any })
                && !!(entity.UnpaidChildSupportAmounts?.length)
        };

        const origStatus: IGroupStatus = storage.getGroupStatus("documents") ?? {};
        const activeTab = (origStatus.activeTab && visibleTabs[origStatus.activeTab])
            ? origStatus.activeTab
            : Object.keys(visibleTabs).find(k => visibleTabs[k]);

        storage.setGroupStatus({ ...origStatus, visibleTabs: { ...visibleTabs }, activeTab }, "documents");
    };

    loadTabsData = async (): Promise<void> => {
        const storage = this.props.storage.getThis() as FormStorage<IPrDeductionEntity, IDeductionCustomData>;

        let depositedDeductions: IDepositedDeduction[];
        let deducted: IDeductedAmounts[] = [];

        const promises = [loadDepositedDeductions(storage)];
        const isDistrainmentDeposit = this.entity.TypeCode === PrDeductionTypeCode.DistrainmentDeposit;
        if (isDistrainmentDeposit) {
            promises.push(loadDepositedDistrainmentAmounts(storage));
        }

        const isInsolvencyDeposit = this.entity.TypeCode === PrDeductionTypeCode.InsolvencyDeposit;

        const result = await Promise.all(promises);

        if (isInsolvencyDeposit) {
            // Insolvency deposit don't have deducted amounts. It reads it as deposited deductions - displayed in different tab.
            depositedDeductions = (result[0] as IPrDepositedDeductionEntity[])
                .map(d => ({...d, Deposit: d.InsolvencyDeposit}));
        } else {
            // other types
            if (isDistrainmentDeposit) {
                depositedDeductions = (result[1] as IDepositedDeduction[]);
            }
            // all other entities than insolvency deposit show deposited deductions as "Deducted" tab.
            // Same datasource as Insolvency deposit, but displayed from the other side.
            deducted = (result[0] as IPrDepositedDeductionEntity[])
                .map(d   => ({
                    ...d,
                    Deposit: d.InsolvencyDeposit,
                    Id: `deposited_${d.Id}`, // to avoid duplicates with PayslipDocuments
                }));

            this.entity.PayslipDocuments?.forEach(pd => {
                deducted.push({
                    Id: `otherLiability_${pd.Id}`,
                    Payslip: pd.Payslip,
                    DepositedAmount: pd.Document.Amount,
                    Document: pd.Document,
                });
            });

            deducted.sort((a, b) => yearMonthSortFunction(a.Payslip, b.Payslip));
        }

        storage.setCustomData({ depositedDeductions, deducted });
        this.setTabsVisibility();
    }

    handleRecipientChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage.getThis();
        prepareSavedAccounts({
            storage,
            type: DocumentTypeCode.InvoiceReceived,
            businessPartner: e.additionalData ?? {},
            businessPartnerPath: PrDeductionEntity.Recipient,
            account: this.entity.BankAccount
        });
        storage.refresh();
    };

    handleCustomLineItemAction = async (args: ISmartFastEntriesActionEvent): Promise<void> => {
        if (args.customActionType === ADD_EMPLOYEE_ITEM) {
            this.props.storage.setCustomData({ pairedDialogOpened: true });
            this.props.storage.refresh();
        }
        if (args.customActionType === INSOLVENCY_DEPOSIT_CLOSING) {
            this.props.storage.setCustomData({
                depositClosingDialogOpen: true,
            });
            this.props.storage.refresh();
        }
    };

    shouldRenderEndDeponation = () => {
        return this.entity.TypeCode === PrDeductionTypeCode.InsolvencyDeposit
            || this.entity.TypeCode === PrDeductionTypeCode.DistrainmentDeposit;
    }

    get deleteEnabled(): boolean {
        const deductions = (this.props.storage.getCustomData() as IDeductionCustomData)?.depositedDeductions;
        return !deductions || deductions?.length === 0;
    }

    shouldEnableEndDeponation = () => {
        const deductions = (this.props.storage.getCustomData() as IDeductionCustomData)?.depositedDeductions;
        return !this.props.storage.data.bindingContext.isNew() && deductions?.length > 0;
    }

    handleReopenDeposit = (): void => {
        const storage = this.props.storage.getThis();
        storage.setValueByPath(PrDeductionEntity.InsolvencyDepositClosing, null);
        storage.setValueByPath(PrDeductionEntity.DistrainmentDepositClosing, null);
        storage.setValueByPath(PrDeductionEntity.DateLegallyEffective, null);
        storage.setValueByPath(PrDeductionEntity.DateEnd, null);
        this.handleSaveClick();
    }

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const _isClosed = isDepositDeductionClosed({ storage });

        return (
            <ButtonGroup wrap={"wrap"}>
                {!_isClosed && (
                    <Button
                        hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent
                    >
                        {storage.t("Common:General.Cancel")}
                    </Button>
                )}
                {this.isDeletable && (
                    <SmartFormDeleteButton
                        isDisabled={!this.deleteEnabled}
                        storage={storage}
                        onClick={this.handleDelete}
                    />
                )}

                {this.shouldAllowCopy() && (
                    <Button
                        hotspotId={"formCopy"}
                        onClick={this.handleCopy}
                        isDisabled={this.shouldDisableCopyButton()}
                        isTransparent
                    >
                        {storage.t("Common:Form.CreateCopy")}
                    </Button>
                )}
                {this.shouldRenderEndDeponation() && !_isClosed && (
                    <Button
                        isTransparent
                        onClick={() => {
                            this.props.storage.setCustomData({
                                depositClosingDialogOpen: true,
                            });
                            this.props.storage.refresh();
                        }}
                        isDisabled={!this.shouldEnableEndDeponation()}
                    >
                        {storage.t("Deduction:Form.EndDeposit")}
                    </Button>
                )}
                {this.shouldRenderEndDeponation() && _isClosed && (
                    <Button
                        onClick={this.handleReopenDeposit}
                        isDisabled={hasDepositClosingDocumentBeenLiquidated(this.entity)}
                        title={storage.t("Deduction:Form.ReopenDepositDisabledTooltip")}
                    >
                        {storage.t("Deduction:Form.ReopenDeposit")}
                    </Button>
                )}
                {!storage.isReadOnly && !_isClosed && (
                    <Button
                        hotspotId={"formSave"}
                        onClick={this.handleSaveClick}
                        isDisabled={this.shouldDisableSaveButton()}
                    >
                        {storage.t("Common:General.Save")}
                    </Button>
                )}
            </ButtonGroup>
        );
    }
    handleLineItemsAction = async (args: ISmartFastEntriesActionEvent): Promise<void> => {
        if (args.actionType === ActionType.Custom) {
            await this.handleCustomLineItemAction(args);
            return;
        }
        this.props.storage.handleLineItemsAction(args);
        this.props.storage.refresh();
    };

    get readOnlyListTitle(): string {
        return this.props.storage.t("Deduction:Pair.Employees");
    }

    handleDateEndChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage.getThis();
        if (e.bindingContext.getPath(true) === PrDeductionEntity.DateEnd && e.triggerAdditionalTasks) {
            const endOfMonth = getUtcDayjs(e.value as Date).clone().endOf("month").toDate();
            storage.setValueByPath(PrDeductionEntity.DateEnd, endOfMonth);
        }
    }

    handleSmartTemporalPropertyDialogChange(args: ISmartFieldChange): void {
        if (args.bindingContext.getPath() === PrDeductionTemporalEntity.CalculationType) {
            this.props.storage.addActiveField(args.bindingContext.getParent().navigate(PrDeductionTemporalEntity.Amount));
        }
    }

    handleChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage.getThis();
        const path = e.bindingContext.getPath(true);

        if (path === PrDeductionEntity.ChildSupportType ||
            path === PrDeductionEntity.IsCompanyOwn ||
            path === PrDeductionTemporalEntity.CalculationType ||
            path === PrDeductionTemporalEntity.IsIncludingAllEmployees ||
            path === PrDeductionEntity.InsolvencyDepositClosingCode) {
            storage.refresh();
        }

        if (e.bindingContext.getNavigationPath(true) === createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Name) && e.triggerAdditionalTasks) {
            this.handleRecipientChange(e);
        }

        storage.handleChange(e);

        this.handleDateEndChange(e);

        handleBankFieldChangeWithoutSavedAccount({ storage }, e);

        handleCurrentTemporalPropChange(e, storage);
        storage.refreshFields(e.triggerAdditionalTasks);
    };

    handleBlur = async (e: ISmartFieldBlur): Promise<void> => {
        if (e.wasChanged) {
            handleBankAccountOrIBANBlur(getBankBlurArgs(this.props.storage.getThis(), e.bindingContext));
        }
        await this.props.storage.handleBlur(e);
        this.props.storage.refreshFields();
    }

    handleEmployeesSelection = async (selected: BindingContext[], entities: IPrEmployeeEntity[]): Promise<boolean> => {
        const { storage } = this.props;
        const employeesBc = storage.data.bindingContext.navigate(PrDeductionEntity.Employees);

        const selectedIds = new Set(selected.map(s => s.getKey()));
        const currentEmployees = this.entity.Employees ?? [];

        const keptEmployees: typeof currentEmployees = [];
        const removedEmployees: typeof currentEmployees = [];
        const currentEmployeeIds = new Set<number>();

        currentEmployees.forEach(emp => {
            const employeeId = emp.Employee?.Id;

            currentEmployeeIds.add(employeeId);

            if (selectedIds.has(employeeId)) {
                keptEmployees.push(emp);
            } else {
                removedEmployees.push(emp);
            }
        });

        if (removedEmployees.length > 0) {
            storage.handleLineItemsAction({
                actionType: ActionType.Remove,
                bindingContext: employeesBc,
                items: keptEmployees,
                affectedItems: removedEmployees
            });
        }

        entities.forEach((entity) => {
            if (!currentEmployeeIds.has(entity.Id)) {
                addNewLineItem(storage, PrDeductionEntity.Employees, PrDeductionEntity.Employees, [], {
                    [PrDeductionEmployeeEntity.Employee]: entity,
                    [PrDeductionEmployeeEntity.DateValidFrom]: this.entity.DateStart,
                    [PrDeductionEmployeeEntity.DateValidTo]: this.entity.DateEnd ?? DATE_MAX
                });
            }
        });

        storage.refresh();
        return true;
    };

    renderCustomDialogs = (): ReactElement => {
        const storage = this.props.storage;
        const customData = storage.getCustomData();
        const bc = storage.data.bindingContext;

        if (customData.pairedDialogOpened) {
            const selectedEmployeesBcs = (this.entity.Employees ?? [])
                .filter(emp => emp.Employee?.Id)
                .map(emp => bc.createNewBindingContext(`${EntitySetName.PrEmployees}(${emp.Employee.Id})`));

            return <SelectionTableViewDialog
                    storage={storage}
                    tableView={PairEmployeeTableView}
                    getDefinition={getEmloyeesDef}
                    title={this.readOnlyListTitle}
                    selectedItems={selectedEmployeesBcs}
                    onSelection={this.handleEmployeesSelection}
                    onClose={this.handleClosePairing}
                    isSingleSelect={false}/>;
        }

        if (customData.depositClosingDialogOpen) {
            const DateEnd = getDateEndFromLastDeduction((customData as IDeductionCustomData).depositedDeductions);
            return (
                <PlugAndPlayForm getDef={getDepositClosingDef}
                                 t={storage.t}
                                 bindingContext={storage.data.bindingContext}
                                 data={{
                                     ...cloneDeep(storage.data.entity)
                                 }}
                                 initialData={{
                                     entity: {
                                         [PrDeductionEntity.DateEnd]: DateEnd
                                     }
                                 }}
                                 formView={DepositClosingFormView}
                                 formViewProps={{
                                     dialogProps: {
                                         title: storage.t("Deduction:Form.EndDeposit"),
                                         aretateWidth: true
                                     },
                                     formProps: {
                                         renderScrollbar: false,
                                         isSimple: true
                                     },
                                     confirmText: storage.t("Common:General.Confirm"),
                                     onAfterConfirm: this.handleConfirmDepositClosingDialog,
                                     onClose: this.handleCloseDepositClosingDialog
                                 }}
                />
            );
        }

        if (customData.otherPaymentsDialogOpen) {
            const initialItems = this.entity.OtherPayments ? cloneDeep(this.entity.OtherPayments) : [];
            if (!initialItems.length) {
                const item = addEmptyLineItem({
                    storage,
                    bindingContext: storage.data.bindingContext.navigate(PrDeductionEntity.OtherPayments),
                    newItemsCount: 1,
                    index: 1,
                    columns: [
                        { id: PrDeductionOtherPaymentEntity.Date },
                        { id: PrDeductionOtherPaymentEntity.Amount },
                        { id: PrDeductionOtherPaymentEntity.Description },
                    ],
                    context: storage.context,
                    order: PrDeductionOtherPaymentEntity.Id
                });
                initialItems.push(item);
            }
            return (
                <PlugAndPlayForm getDef={getOtherPaymentsDef}
                                 t={storage.t}
                                 bindingContext={storage.data.bindingContext}
                                 data={{
                                     [PrDeductionEntity.OtherPayments]: initialItems
                                 }}
                                 formView={OtherPaymentsFormView}
                                 formViewProps={{
                                     dialogProps: {
                                         title: storage.t("Deduction:Form.AddOtherPayment"),
                                         aretateWidth: true
                                     },
                                     formProps: {
                                         renderScrollbar: false,
                                         isSimple: true
                                     },
                                     confirmText: storage.t("Deduction:Form.OtherPaymentCreate"),
                                     onAfterConfirm: this.handleOtherPaymentsChange,
                                     onClose: this.handleOtherPaymentsDialogClose
                                 }}
                />
            );
        }

        return null;
    };

    handleOtherPaymentsChange = (): void => {
        this.props.storage.reload({ preserveInfos: true });
        this.handleOtherPaymentsDialogClose();
    };

    handleOtherPaymentsDialogClose = (): void => {
        this.props.storage.setCustomData({ otherPaymentsDialogOpen: false });
        this.props.storage.refresh();
    };

    handleClosePairing = (): void => {
        this.props.storage.setCustomData({ pairedDialogOpened: false });
        this.props.storage.refresh();
    };

    handleConfirmDepositClosingDialog = (): void => {
        this.props.storage.reload({ preserveInfos: true });
        this.handleCloseDepositClosingDialog();
    };

    handleCloseDepositClosingDialog = (): void => {
        this.props.storage.setCustomData({ depositClosingDialogOpen: false });
        this.props.storage.refresh();
    };

    onBeforeSave(): IPrDeductionEntity {
        const storage = this.props.storage.getThis();
        const data = cloneDeep(this.entity);
        if (data.TypeCode === PrDeductionTypeCode.Custom && data.IsCompanyOwn) {
            data.Recipient = null;
        } else if (data.Recipient) {
            data.Recipient.BusinessPartner = null; // we don't want to have reference to BP, just use it as a template
        }

        if ([PrDeductionTypeCode.Distrainment, PrDeductionTypeCode.Insolvency].includes(this.entity.TypeCode)) {
            delete data.TemporalPropertyBag;
        }

        // Maybe move to BE in future?
        if (this.entity.TypeCode === PrDeductionTypeCode.InsolvencyAdministratorReward ||
            (this.entity.TypeCode === PrDeductionTypeCode.ChildSupport && this.entity.ChildSupportTypeCode === PrChildSupportDeductionTypeCode.Regular)) {
            data.TemporalPropertyBag?.forEach(bag => {
                bag.CalculationTypeCode = PrDeductionCalculationTypeCode.Fixed;
            });
            if (data.CurrentTemporalPropertyBag) {
                data.CurrentTemporalPropertyBag.CalculationTypeCode = PrDeductionCalculationTypeCode.Fixed;
            }
        }
        if (hasEmptyBankAccount(storage) || !isVisibleByPath(storage, createPath(PrDeductionEntity.BankAccount, PrDeductionBankAccountEntity.Country))) {
            data.BankAccount = null;
        }
        return data;
    }
}


export default withPermissionContext(DeductionFormView);
